/**
 * Accounting V2 reports — one screen for every report.
 *
 * Nothing here knows what a Trial Balance is. Filters, columns, totals,
 * assertions and drilldown targets all come from the server's report definition
 * (accounting/reports/registry.js), so adding a report to the registry makes it
 * appear here with no change to this file.
 *
 * Column visibility is chosen at runtime by the user, seeded from each column's
 * declared `defaultVisible`.
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { getApiErrorMessage } from '@/shared/api/axios';
import { ROUTES } from '@/routes/paths';
import {
  groupDefinitions,
  initialFilterState,
  useReportDefinitions,
  useReportRun,
  type ColumnFormat,
  type ReportColumn,
  type ReportDefinition,
  type ReportRow,
} from '../hooks/useReportRegistry';

const CELL = 'px-3 py-2 align-middle';
const HEAD = 'px-3 py-2 text-xs font-medium uppercase tracking-wide text-on-surface-variant';

/** Format a value per the column's declared format. Minor units divide by 100. */
function formatValue(value: unknown, format: ColumnFormat): string {
  if (value === null || value === undefined || value === '') return '—';

  if (format === 'money_minor') {
    const minor = Number(value);
    if (!Number.isFinite(minor)) return '—';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(minor / 100);
  }

  if (format === 'integer') {
    const n = Number(value);
    return Number.isFinite(n) ? new Intl.NumberFormat('en-IN').format(n) : '—';
  }

  if (format === 'date') {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime())
      ? '—'
      : d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' });
  }

  return String(value);
}

function StatusPill({ value }: { value: unknown }) {
  const status = String(value ?? '');
  const tone =
    status === 'pass' || status === 'posted'
      ? 'bg-emerald-50 text-emerald-700'
      : status === 'fail'
        ? 'bg-rose-50 text-rose-700'
        : status === 'not_implemented'
          ? 'bg-slate-100 text-slate-600'
          : 'bg-amber-50 text-amber-700';
  return (
    <span className={`rounded px-2 py-0.5 text-xs ${tone}`}>
      {status === 'not_implemented' ? 'not verified' : status || '—'}
    </span>
  );
}

function Loading() {
  return (
    <div className="space-y-2 py-4">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
    </div>
  );
}

/** Renders a failed run without leaking internals. */
function RunError({ message }: { message?: string }) {
  return (
    <div className="py-8 text-center">
      <p className="text-sm font-medium text-rose-600">Could not generate this report.</p>
      <p className="mt-1 text-xs text-on-surface-variant">{message || 'Please retry in a moment.'}</p>
    </div>
  );
}

/**
 * A cell, with drilldown when the definition declares one for this column.
 *
 * Drilldown is real navigation into the Ledger Explorer, carrying the ids the
 * definition names — not a placeholder link.
 */
function Cell({
  row,
  column,
  definition,
}: {
  row: ReportRow;
  column: ReportColumn;
  definition: ReportDefinition;
}) {
  const raw = row[column.id];
  const text = formatValue(raw, column.format);

  if (column.format === 'status') return <StatusPill value={raw} />;

  const drill = definition.drilldown;
  if (drill && drill.from === column.id) {
    const params = new URLSearchParams();
    for (const [param, sourceField] of Object.entries(drill.carry)) {
      const value = row[sourceField];
      if (value !== undefined && value !== null && value !== '') params.set(param, String(value));
    }
    if (params.toString()) {
      return (
        <Link
          to={`${ROUTES.accountsTransactions}?${params.toString()}`}
          className="text-primary underline-offset-2 hover:underline"
        >
          {text}
        </Link>
      );
    }
  }

  return <>{text}</>;
}

/** Filter controls generated from the definition. */
function FilterPanel({
  definition,
  values,
  onChange,
  onRun,
  running,
}: {
  definition: ReportDefinition;
  values: Record<string, string>;
  onChange: (id: string, value: string) => void;
  onRun: () => void;
  running: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      {definition.filters.map((filter) => {
        if (filter.type === 'enum') {
          return (
            <Select
              key={filter.id}
              label={filter.label}
              value={values[filter.id] ?? ''}
              onChange={(e) => onChange(filter.id, e.target.value)}
              options={(filter.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
              className="min-w-[160px]"
            />
          );
        }
        return (
          <Input
            key={filter.id}
            label={filter.required ? `${filter.label} *` : `${filter.label} (optional)`}
            type={filter.type === 'date' ? 'date' : 'text'}
            value={values[filter.id] ?? ''}
            onChange={(e) => onChange(filter.id, e.target.value)}
            hint={filter.type === 'date' && !values[filter.id] ? 'Blank uses the server default' : undefined}
            className="min-w-[160px]"
          />
        );
      })}
      <Button onClick={onRun} loading={running}>Run report</Button>
    </div>
  );
}

/** The runner: filters, column visibility, assertions, totals and rows. */
function ReportRunner({ definition }: { definition: ReportDefinition }) {
  const [filters, setFilters] = useState<Record<string, string>>(() => initialFilterState(definition));
  const [applied, setApplied] = useState<Record<string, string>>(() => initialFilterState(definition));
  const [hidden, setHidden] = useState<Set<string>>(
    () => new Set(definition.columns.filter((c) => c.defaultVisible === false).map((c) => c.id)),
  );

  const { data, isLoading, isFetching, isError, error } = useReportRun(definition.id, applied);

  const visibleColumns = useMemo(
    () => definition.columns.filter((c) => !hidden.has(c.id)),
    [definition.columns, hidden],
  );

  const toggleColumn = (id: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rows = data?.rows ?? [];
  const meta = data?.meta ?? {};
  const summary = (meta.summary ?? {}) as Record<string, unknown>;

  if (definition.id === 'gstr-2b') {
    return <Gstr2bReportComponent />;
  }
  if (definition.id === 'gstr-3b') {
    return <Gstr3bReportComponent />;
  }
  if (definition.id.startsWith('gstr-')) {
    return <Gstr1SummaryReport />;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardBody className="space-y-3">
          <div>
            <h2 className="text-data-lg font-semibold text-on-surface">{definition.label}</h2>
            {definition.description ? (
              <p className="text-body-sm text-on-surface-variant">{definition.description}</p>
            ) : null}
          </div>

          {/* The server states its own incompleteness; surface it rather than
              letting the figures imply more than they support. */}
          {definition.maturity === 'partial' && definition.limitations?.length ? (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <p className="font-medium">This report is incomplete.</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4">
                {definition.limitations.map((l) => <li key={l}>{l}</li>)}
              </ul>
            </div>
          ) : null}

          <FilterPanel
            definition={definition}
            values={filters}
            onChange={(id, value) => setFilters((prev) => ({ ...prev, [id]: value }))}
            onRun={() => setApplied({ ...filters })}
            running={isFetching}
          />
        </CardBody>
      </Card>

      {isLoading ? <Loading /> : isError ? <RunError message={getApiErrorMessage(error)} /> : (
        <>
          {/* Assertions: the report's own statement about whether it balances. */}
          {definition.assertions?.length ? (
            <div className="flex flex-wrap gap-2">
              {definition.assertions.map((a) => {
                const ok = summary[a.id] === true;
                const imbalance = a.imbalanceField ? summary[a.imbalanceField] : undefined;
                return (
                  <span
                    key={a.id}
                    className={`rounded px-2.5 py-1 text-xs ${ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
                  >
                    {ok ? '✓' : '✕'} {a.label}
                    {!ok && imbalance !== undefined && imbalance !== 0
                      ? ` (${formatValue(imbalance, 'money_minor')})`
                      : ''}
                  </span>
                );
              })}
            </div>
          ) : null}

          {/* Custom design renderers for balance-sheet, cash-flow, and ledger-master */}
          {definition.id === 'balance-sheet' ? (
            <BalanceSheetSectionReport asOfDate={applied.asOfDate || filters.asOfDate} />
          ) : definition.id === 'cash-flow' ? (
            <CashFlow startDate={applied.startDate || filters.startDate} endDate={applied.endDate || filters.endDate} />
          ) : definition.id === 'ledger-master' ? (
            <LedgerMasterReport />
          ) : definition.summaryFields?.length ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {definition.summaryFields.map((field) => (
                <Card key={field.id}>
                  <CardBody>
                    <p className="text-label-caps uppercase text-on-surface-variant">{field.label}</p>
                    <p className="mt-1 text-data-lg font-semibold text-on-surface">
                      {formatValue(summary[field.id], field.format)}
                    </p>
                  </CardBody>
                </Card>
              ))}
            </div>
          ) : null}

          {definition.columns.length > 0 ? (
            <Card>
              <CardBody className="space-y-3">
                <details className="rounded border border-outline bg-surface-variant/40 p-3">
                  <summary className="cursor-pointer text-data-md font-medium text-on-surface">
                    Columns ({visibleColumns.length} of {definition.columns.length})
                  </summary>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {definition.columns.map((c) => (
                      <label key={c.id} className="flex cursor-pointer items-center gap-1.5 text-body-sm text-on-surface">
                        <input type="checkbox" checked={!hidden.has(c.id)} onChange={() => toggleColumn(c.id)} />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </details>

                <div className="flex flex-wrap items-center justify-between gap-2 text-body-sm text-on-surface-variant">
                  <span>{meta.rowCount ?? rows.length} row(s)</span>
                  {meta.generatedAt ? (
                    <span>Generated {new Date(meta.generatedAt).toLocaleString('en-IN')}</span>
                  ) : null}
                </div>

                {rows.length === 0 ? (
                  <div className="py-10 text-center">
                    <p className="text-sm font-medium text-on-surface-variant">No rows for these filters</p>
                    <p className="mt-1 text-xs text-on-surface-variant">
                      Financial reports include posted journals only, so unposted work never appears.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="border-b border-outline">
                        <tr>
                          {visibleColumns.map((c) => (
                            <th key={c.id} className={`${HEAD} ${c.align === 'right' ? 'text-right' : ''}`}>
                              {c.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, i) => (
                          <tr key={String(row._id ?? row.code ?? row.name ?? i)} className="border-b border-outline/60">
                            {visibleColumns.map((c) => (
                              <td key={c.id} className={`${CELL} ${c.align === 'right' ? 'text-right' : ''}`}>
                                <Cell row={row} column={c} definition={definition} />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}

export function AccountingReportsPage() {
  const { data, isLoading, isError, error } = useReportDefinitions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [navSearch, setNavSearch] = useState('');
  const selectedId = searchParams.get('report') ?? '';

  const definitions = data?.items ?? [];
  const selected = definitions.find((d) => d.id === selectedId) ?? null;

  const filteredGroups = useMemo(() => {
    const groups = groupDefinitions(definitions);
    if (!navSearch.trim()) return groups;
    const q = navSearch.toLowerCase();
    return groups
      .map(({ group, reports }) => ({
        group,
        reports: reports.filter((r) => r.label.toLowerCase().includes(q) || r.id.toLowerCase().includes(q)),
      }))
      .filter((g) => g.reports.length > 0);
  }, [definitions, navSearch]);

  if (isLoading) return <div className="p-6"><Loading /></div>;
  if (isError) {
    return (
      <div className="p-6">
        <RunError message={getApiErrorMessage(error)} />
      </div>
    );
  }

  if (definitions.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm font-medium text-on-surface-variant">No reports available</p>
        <p className="mt-1 text-xs text-on-surface-variant">
          Reports are granted individually. Ask an administrator for the relevant reporting permission.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1580px] space-y-5 pb-16">
      <PageHeader
        icon={BarChart3}
        eyebrow={selected ? (
          <button onClick={() => setSearchParams({})} className="text-primary hover:underline">← All reports</button>
        ) : 'Finance'}
        title={selected ? selected.label : 'Financial Reports'}
        description={selected
          ? selected.description || 'Live report calculations from posted journal entries.'
          : 'Financial statements, GST returns, ledgers, registers and receivable/payable summaries from posted entries.'}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* Removed on purpose. This used to be a "Reporting period" input plus
                This Month / FY 2026-27 / Q2 buttons, and nothing about it was true:
                the string was never sent anywhere (each report runs through
                useReportRun with its OWN filters at :194-199), the default was the
                hardcoded '2026-07-01 - 2026-07-31', "This Month" set that same July
                string, and "Q2" set the entire financial year. A period control that
                filters nothing on an accounting screen is worse than no control, so
                the real per-report filters are pointed at instead. */}
            <span className="text-body-sm text-on-surface-subtle">
              Each report sets its own period below.
            </span>
            {selectedId && (
              <Button variant="outline" size="sm" onClick={() => setSearchParams({})}>
                Dashboard Overview
              </Button>
            )}
          </div>
        }
      />


      {/* Horizontal Flex Report Directory Bar */}
      <nav aria-label="Reports" className="bg-white dark:bg-slate-950 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4 w-full overflow-hidden">
        {/* Header Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-900">
          <div className="flex items-center gap-2.5">
            <span className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-slate-100">
              REPORT DIRECTORY
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200/80 dark:border-indigo-800">
              {definitions.length} Reports
            </span>
          </div>
          <div className="relative w-full sm:w-72">
            <Input
              type="text"
              placeholder="Search reports..."
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              className="h-8 text-xs w-full bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>
        </div>

        {/* Aligned Grid Rows for Categories & Buttons */}
        <div className="space-y-3">
          {filteredGroups.map(({ group, reports }) => (
            <div key={group} className="grid grid-cols-1 md:grid-cols-[170px_1fr] items-start md:items-center gap-2 md:gap-4 py-1 border-b border-slate-50 dark:border-slate-900/50 last:border-none">
              <div className="shrink-0 w-full max-w-[170px]">
                <span className="inline-block w-full text-[10px] font-extrabold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/60 px-2.5 py-1 rounded-lg border border-indigo-200/80 dark:border-indigo-900/50 text-center truncate">
                  {group}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap min-w-0 w-full">
                {reports.map((d) => {
                  const isActive = d.id === selectedId;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setSearchParams({ report: d.id })}
                      aria-current={isActive ? 'true' : undefined}
                      className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition-all shrink-0 border ${
                        isActive
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-500/20'
                          : 'bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200/80 dark:border-slate-800 hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-950/60 dark:hover:text-indigo-300 hover:border-indigo-200'
                      }`}
                    >
                      <span className="truncate">{d.label}</span>
                      {d.maturity === 'partial' && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isActive ? 'bg-white/20 text-white' : 'text-amber-600 bg-amber-50 dark:bg-amber-950/60'}`} title="Incomplete">
                          partial
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </nav>

      {/* Main Report Runner / Dashboard Content */}
      <div className="w-full">
        {selected ? (
          <ReportRunner key={selected.id} definition={selected} />
        ) : (
          <FinancialReportsDashboard onSelectReport={(id) => setSearchParams({ report: id })} />
        )}
      </div>

    </div>
  );
}

import { useBalanceSheet, ReportAccountBalance } from '@/features/accounts/hooks/useReports';
import { CashFlow } from '@/features/accounts/components/reports/CashFlow';
import { LedgerMasterReport } from '@/features/accounts/components/reports/LedgerMasterReport';
import { FinancialReportsDashboard } from '@/features/accounts/components/reports/FinancialReportsDashboard';
import { Gstr1SummaryReport } from '@/features/accounts/components/reports/Gstr1SummaryReport';
import { Gstr2bReportComponent } from '@/features/accounts/components/reports/Gstr2bReportComponent';
import { Gstr3bReportComponent } from '@/features/accounts/components/reports/Gstr3bReportComponent';
import {
  Building2,
  HandCoins,
  PieChart,
  Percent,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  FileText,
  Info,
  Table as TableIcon
} from 'lucide-react';

const FINANCIAL_YEARS = [
  { label: 'FY 2026-2027', endDate: '2027-03-31' },
  { label: 'FY 2025-2026', endDate: '2026-03-31' },
  { label: 'FY 2024-2025', endDate: '2025-03-31' },
];

function BalanceSheetSectionReport({ asOfDate }: { asOfDate?: string }) {
  const [selectedFY, setSelectedFY] = useState('FY 2026-2027');
  const [currentAsOfDate, setCurrentAsOfDate] = useState(asOfDate || '2027-03-31');
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);
  const [isLiabilitiesOpen, setIsLiabilitiesOpen] = useState(true);
  const [isAssetsOpen, setIsAssetsOpen] = useState(true);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  const { data, isLoading, error } = useBalanceSheet(currentAsOfDate);

  const handleFYChange = (fyLabel: string) => {
    setSelectedFY(fyLabel);
    const fy = FINANCIAL_YEARS.find(f => f.label === fyLabel);
    if (fy) setCurrentAsOfDate(fy.endDate);
  };

  const formatCurrency = (val: number) => {
    return `₹${(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const calcPercentage = (val: number, total: number) => {
    if (!total || total === 0) return '0.0%';
    const pct = (val / total) * 100;
    return `${pct.toFixed(1)}%`;
  };

  const handleDownloadCSV = () => {
    if (!data) return;
    const rows = [
      ['Balance Sheet', `As of ${currentAsOfDate}`],
      [''],
      ['ASSETS', 'Amount (INR)', '% of Total'],
      ...data.assets.map(a => [a.name, a.netBalance, calcPercentage(a.netBalance, data.totalAssets)]),
      ['Total Assets', data.totalAssets, '100%'],
      [''],
      ['LIABILITIES AND EQUITIES', 'Amount (INR)', '% of Total'],
      ...data.liabilities.map(l => [l.name, l.netBalance, calcPercentage(l.netBalance, data.totalLiabilitiesAndEquity)]),
      ...data.equity.map(e => [e.name, e.netBalance, calcPercentage(e.netBalance, data.totalLiabilitiesAndEquity)]),
      ['Current Year Net Profit', data.netProfit, calcPercentage(data.netProfit, data.totalLiabilitiesAndEquity)],
      ['Total Liabilities & Equities', data.totalLiabilitiesAndEquity, '100%'],
    ];

    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Balance_Sheet_${selectedFY.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowDownloadMenu(false);
  };

  if (isLoading) return <Skeleton className="h-[400px] w-full rounded-xl" />;
  if (error || !data) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 text-sm font-medium">
        Failed to load Balance Sheet.
      </div>
    );
  }

  const totalCapital = data.totalEquity + data.netProfit;
  const debtToEquityRatio = totalCapital > 0 
    ? ((data.totalLiabilities / totalCapital) * 100).toFixed(0) + '%' 
    : '0%';

  return (
    <div className="space-y-6 pt-2">
      {/* Top Filter Controls Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-1">
        <div className="flex flex-col">
          <label className="text-xs text-muted-foreground font-medium mb-1">Select Financial Year</label>
          <select
            value={selectedFY}
            onChange={(e) => handleFYChange(e.target.value)}
            className="border border-input rounded-md text-sm px-3 py-2 bg-background font-medium shadow-sm w-52"
          >
            {FINANCIAL_YEARS.map(fy => (
              <option key={fy.label} value={fy.label}>{fy.label}</option>
            ))}
          </select>
        </div>

        {/* Download As Dropdown */}
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDownloadMenu(!showDownloadMenu)}
            className="h-9 px-4 font-medium flex items-center gap-2 border-input shadow-sm"
          >
            Download As
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </Button>

          {showDownloadMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-lg z-50 py-1 text-sm">
              <button
                onClick={handleDownloadCSV}
                className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-2 text-foreground font-medium"
              >
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                CSV Spreadsheet
              </button>
              <button
                onClick={() => { window.print(); setShowDownloadMenu(false); }}
                className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-2 text-foreground font-medium"
              >
                <FileText className="w-4 h-4 text-blue-600" />
                PDF / Print View
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Summary KPI Banner */}
      <div className="bg-slate-50/80 dark:bg-slate-900/50 rounded-xl border border-border/80 p-4 space-y-4">
        <button
          onClick={() => setIsSummaryOpen(!isSummaryOpen)}
          className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
        >
          {isSummaryOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span>Summary</span>
          <span className="text-xs text-muted-foreground font-normal">Figures are in INR (₹)</span>
        </button>

        {isSummaryOpen && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-1">
            <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-border/60 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 rounded-lg">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  Total Assets <Info className="w-3 h-3 text-muted-foreground/60" />
                </div>
                <div className="text-base font-bold font-mono text-foreground mt-0.5">
                  {formatCurrency(data.totalAssets)}
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-border/60 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-rose-50 dark:bg-rose-950/40 text-rose-600 rounded-lg">
                <HandCoins className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  Total Liabilities <Info className="w-3 h-3 text-muted-foreground/60" />
                </div>
                <div className="text-base font-bold font-mono text-foreground mt-0.5">
                  {formatCurrency(data.totalLiabilities)}
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-border/60 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 rounded-lg">
                <PieChart className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  Total Capital <Info className="w-3 h-3 text-muted-foreground/60" />
                </div>
                <div className="text-base font-bold font-mono text-foreground mt-0.5">
                  {formatCurrency(totalCapital)}
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-border/60 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-950/40 text-blue-600 rounded-lg">
                <Percent className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  Debt to Equity Ratio <Info className="w-3 h-3 text-muted-foreground/60" />
                </div>
                <div className="text-base font-bold font-mono text-foreground mt-0.5">
                  {debtToEquityRatio}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hierarchical Balance Sheet Table (Image 2 style) */}
      <div className="bg-white dark:bg-slate-950 rounded-xl border border-border overflow-hidden shadow-sm space-y-0">
        {/* Table Header Controls */}
        <div className="px-4 py-3 bg-slate-50/70 dark:bg-slate-900/60 border-b border-border flex items-center justify-between text-xs text-muted-foreground font-medium">
          <span>Showing 1 to 2 of 2 entries</span>
          <div className="p-1 rounded border border-border bg-background">
            <TableIcon className="w-4 h-4 text-muted-foreground" />
          </div>
        </div>

        {/* Table Head */}
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-slate-50 dark:bg-slate-900 border-b border-border text-muted-foreground font-semibold text-xs uppercase tracking-wide">
            <tr>
              <th className="px-5 py-3">Accounts</th>
              <th className="px-5 py-3 text-right">Amount</th>
              <th className="px-5 py-3 text-right">% of Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {/* Row 1: Asset */}
            <tr className="hover:bg-slate-50/60 dark:hover:bg-slate-900/40 transition-colors">
              <td className="px-5 py-3.5 font-bold text-foreground flex items-center gap-2">
                <button onClick={() => setIsAssetsOpen(!isAssetsOpen)} className="hover:text-primary transition-colors">
                  {isAssetsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <span>1. Asset</span>
              </td>
              <td className="px-5 py-3.5 text-right font-mono font-bold text-foreground">
                {formatCurrency(data.totalAssets)}
              </td>
              <td className="px-5 py-3.5 text-right font-mono text-muted-foreground">
                {calcPercentage(data.totalAssets, data.totalAssets)}
              </td>
            </tr>

            {/* Sub-rows for Assets */}
            {isAssetsOpen && data.assets.map((acc: ReportAccountBalance) => (
              <tr key={acc.accountId} className="bg-slate-50/30 dark:bg-slate-900/20 hover:bg-slate-100/60 dark:hover:bg-slate-900/60 transition-colors">
                <td className="px-5 py-2.5 pl-12 text-xs font-medium text-foreground flex items-center gap-2">
                  <span className="font-mono text-muted-foreground text-[11px]">{acc.code}</span>
                  <span>{acc.name}</span>
                </td>
                <td className="px-5 py-2.5 text-right font-mono text-xs text-foreground font-medium">
                  {formatCurrency(acc.netBalance)}
                </td>
                <td className="px-5 py-2.5 text-right font-mono text-xs text-muted-foreground">
                  {calcPercentage(acc.netBalance, data.totalAssets)}
                </td>
              </tr>
            ))}

            {/* Row 2: Liabilities and equities */}
            <tr className="hover:bg-slate-50/60 dark:hover:bg-slate-900/40 transition-colors">
              <td className="px-5 py-3.5 font-bold text-foreground flex items-center gap-2">
                <button onClick={() => setIsLiabilitiesOpen(!isLiabilitiesOpen)} className="hover:text-primary transition-colors">
                  {isLiabilitiesOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <span>2. Liabilities and equities</span>
              </td>
              <td className="px-5 py-3.5 text-right font-mono font-bold text-foreground">
                {formatCurrency(data.totalLiabilitiesAndEquity)}
              </td>
              <td className="px-5 py-3.5 text-right font-mono text-muted-foreground">
                {calcPercentage(data.totalLiabilitiesAndEquity, data.totalLiabilitiesAndEquity)}
              </td>
            </tr>

            {/* Sub-rows for Liabilities & Equity */}
            {isLiabilitiesOpen && (
              <>
                {/* 1. Liability */}
                <tr className="bg-slate-50/40 dark:bg-slate-900/30 font-semibold text-xs">
                  <td className="px-5 py-2.5 pl-10 text-slate-800 dark:text-slate-200">1. Liability</td>
                  <td className="px-5 py-2.5 text-right font-mono text-slate-800 dark:text-slate-200">{formatCurrency(data.totalLiabilities)}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-muted-foreground">{calcPercentage(data.totalLiabilities, data.totalLiabilitiesAndEquity)}</td>
                </tr>

                {data.liabilities.map((acc: ReportAccountBalance) => (
                  <tr key={acc.accountId} className="bg-slate-50/20 dark:bg-slate-900/10 hover:bg-slate-100/50 dark:hover:bg-slate-900/50 transition-colors">
                    <td className="px-5 py-2 pl-16 text-xs text-foreground flex items-center gap-2">
                      <span className="font-mono text-muted-foreground text-[11px]">{acc.code}</span>
                      <span>{acc.name}</span>
                    </td>
                    <td className="px-5 py-2 text-right font-mono text-xs text-foreground">{formatCurrency(acc.netBalance)}</td>
                    <td className="px-5 py-2 text-right font-mono text-xs text-muted-foreground">{calcPercentage(acc.netBalance, data.totalLiabilitiesAndEquity)}</td>
                  </tr>
                ))}

                {/* 2. Capital */}
                <tr className="bg-slate-50/40 dark:bg-slate-900/30 font-semibold text-xs">
                  <td className="px-5 py-2.5 pl-10 text-slate-800 dark:text-slate-200">2. Capital</td>
                  <td className="px-5 py-2.5 text-right font-mono text-slate-800 dark:text-slate-200">{formatCurrency(totalCapital)}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-muted-foreground">{calcPercentage(totalCapital, data.totalLiabilitiesAndEquity)}</td>
                </tr>

                {data.equity.map((acc: ReportAccountBalance) => (
                  <tr key={acc.accountId} className="bg-slate-50/20 dark:bg-slate-900/10 hover:bg-slate-100/50 dark:hover:bg-slate-900/50 transition-colors">
                    <td className="px-5 py-2 pl-16 text-xs text-foreground flex items-center gap-2">
                      <span className="font-mono text-muted-foreground text-[11px]">{acc.code}</span>
                      <span>{acc.name}</span>
                    </td>
                    <td className="px-5 py-2 text-right font-mono text-xs text-foreground">{formatCurrency(acc.netBalance)}</td>
                    <td className="px-5 py-2 text-right font-mono text-xs text-muted-foreground">{calcPercentage(acc.netBalance, data.totalLiabilitiesAndEquity)}</td>
                  </tr>
                ))}

                {/* Current Year Net Profit Row */}
                <tr className="bg-emerald-50/40 dark:bg-emerald-950/20 hover:bg-emerald-50/80 transition-colors">
                  <td className="px-5 py-2 pl-16 text-xs font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
                    <span className="font-mono text-[10px] bg-emerald-100 dark:bg-emerald-900/60 px-1 rounded text-emerald-800 dark:text-emerald-300">AUTO</span>
                    <span>Current Year Net Profit</span>
                  </td>
                  <td className="px-5 py-2 text-right font-mono text-xs font-bold text-emerald-700 dark:text-emerald-400">{formatCurrency(data.netProfit)}</td>
                  <td className="px-5 py-2 text-right font-mono text-xs text-emerald-600/80">{calcPercentage(data.netProfit, data.totalLiabilitiesAndEquity)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>

        {/* Footer Pagination Bar */}
        <div className="px-4 py-3 bg-slate-50/70 dark:bg-slate-900/60 border-t border-border text-xs text-muted-foreground font-medium">
          Showing 1 to 2 of 2 entries
        </div>
      </div>

      {/* Footer Note */}
      <div className="text-center text-xs text-muted-foreground pt-2 font-medium">
        * Reports are in your business currency INR
      </div>
    </div>
  );
}

